/**
 * FormSelect Component
 * 
 * A reusable select dropdown component using react-select for consistent styling
 * and behavior across all forms. Supports single and multi-select options.
 * 
 * Design Principles:
 * - Consistent with FormInput styling
 * - Clean dropdown interface
 * - Accessible with proper ARIA attributes
 * - Supports loading and disabled states
 */

import React from 'react';
import Select, { MultiValue, SingleValue, ActionMeta } from 'react-select';
import './FormSelect.css';
import { SelectOption } from '@frontend/Models/Form/FormModels';

export interface FormSelectProps {
  /** Label text for the select */
  label: string;
  /** Select name attribute */
  name: string;
  /** Array of select options */
  options: SelectOption[];
  /** Currently selected value (use empty string, not undefined, to keep the input controlled) */
  value: string | undefined;
  /** Change handler - receives the selected value string */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the select is clearable */
  isClearable?: boolean;
  /** Whether the select is in loading state */
  isLoading?: boolean;
  /** Whether multiple selections are allowed */
  isMulti?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Error message to display */
  error?: string;
  /** Optional custom render for each option (e.g. label + tag pill) */
  formatOptionLabel?: (option: SelectOption, context: { context: string }) => React.ReactNode;
  /** Portal the menu to <body> (fixed-position, above modal overlays). Opt-in for selects
   * inside scrolling modals, where an in-container menu overflows the dialog and the user must
   * scroll the MODAL to reach the last options. Off by default — inline menus scroll with
   * their page. */
  portalMenu?: boolean;
}

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  name,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isClearable = false,
  isLoading = false,
  isMulti = false,
  required = false,
  disabled = false,
  className = '',
  error,
  formatOptionLabel,
  portalMenu = false,
}) => {
  const handleChange = (
    newValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    // Check if it's an array (MultiValue) first to narrow the type
    if (Array.isArray(newValue)) {
      // Multi-select case
      if (isMulti) {
        onChange(newValue.map(opt => opt.value).join(','));
      } else {
        onChange('');
      }
    } else {
      // Single select case - newValue is SingleValue<SelectOption> (SelectOption | null)
      const singleValue = newValue as SingleValue<SelectOption>;
      if (singleValue) {
        onChange(singleValue.value ?? '');
      } else {
        onChange('');
      }
    }
  };

  const getValue = () => {
    const safeValue = value ?? '';
    if (isMulti) {
      // For multi-select, split value by comma and find matching options
      const valueArray = safeValue ? safeValue.split(',').filter(v => v) : [];
      return options.filter(opt => valueArray.includes(opt.value));
    }
    // For single select, find the matching option
    return options.find(opt => opt.value === safeValue) || null;
  };

  return (
    <div className={`form-group form-select-group ${className}`}>
      <label htmlFor={name} className="form-group-label">
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>
      <Select
        inputId={name}
        name={name}
        options={options}
        value={getValue()}
        onChange={handleChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isLoading={isLoading}
        isMulti={isMulti}
        isDisabled={disabled}
        className={`react-select-container ${error ? 'select-error' : ''}`}
        classNamePrefix="react-select"
        // Portal (opt-in): the menu mounts on <body> and floats over modal overlays instead of
        // overflowing their scroll container. react-select puts an INLINE z-index on the portal
        // element, so the override must ride the styles API, not a stylesheet.
        menuPortalTarget={portalMenu ? document.body : undefined}
        menuPosition={portalMenu ? 'fixed' : undefined}
        styles={portalMenu
          ? { menuPortal: (base) => ({ ...base, zIndex: 1100 }) }
          : undefined}
        formatOptionLabel={formatOptionLabel}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {error && (
        <span id={`${name}-error`} className="error-message" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default FormSelect;

